import { useEffect, useRef, useCallback } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { sampleTerrainRadius } from '@/world/TerrainSphere';
import { updatePlayerPosition } from '@/state/playerPosition';
import { CombatController } from '@/combat/CombatController';
import { CharacterModel } from '@/entities/CharacterModel';

const MOUSE_SENSITIVITY = 0.002;
const PITCH_MIN = -0.3;
const PITCH_MAX = 1.2;
const HEIGHT_LERP_RATE = 50;

const WALK_SPEED = 20;
const SPRINT_SPEED = 80;
const WALK_ANIM_SPEED = 9;    // rad/s for walk cycle
const SPRINT_ANIM_SPEED = 16; // rad/s for sprint cycle

const CAM_DISTANCE = 32;
const CAM_BASE_HEIGHT = 8;
const CAM_TERRAIN_CLEARANCE = 1.5;

const CAM_POS_LERP = 20;
const CAM_ROT_LERP = 25;

// Pre-allocated temporaries
const _normal = new THREE.Vector3();
const _charPos = new THREE.Vector3();
const _camOffset = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();
const _charLookTarget = new THREE.Vector3();
const _targetCamPos = new THREE.Vector3();
const _targetCamQ = new THREE.Quaternion();
const _lookAtMatrix = new THREE.Matrix4();

interface FirstPersonCameraProps {
  sphereRadius: number;
  startTheta?: number;
  startPhi?: number;
  seed?: number;
}

export function FirstPersonCamera({
  sphereRadius,
  startTheta = 0.72,
  startPhi = 1.4,
  seed = 42,
}: FirstPersonCameraProps) {
  const { camera, gl } = useThree();
  const canvas = gl.domElement;
  const charRef = useRef<THREE.Group>(null);

  const thetaRef = useRef(startTheta);
  const phiRef = useRef(startPhi);

  // yawRef = camera orbit yaw (and character facing direction)
  const yawRef = useRef(0);
  const pitchRef = useRef(0.35);

  const keysRef = useRef({ w: false, s: false, a: false, d: false, shift: false });
  const lockedRef = useRef(false);
  const smoothGroundR = useRef(0);

  // Walk animation state
  const walkPhaseRef = useRef(0);
  const isMovingRef = useRef(false);

  // Exposed to CombatController
  const combatNormalRef = useRef(new THREE.Vector3(0, 1, 0));
  const combatPosRef = useRef(new THREE.Vector3());
  const combatFwdRef = useRef(new THREE.Vector3(0, 0, -1));
  const combatRightRef = useRef(new THREE.Vector3(1, 0, 0));

  const handleDodge = useCallback((dTheta: number, _dPhi: number) => {
    const sinPhi = Math.sin(phiRef.current);
    const cosPhi = Math.cos(phiRef.current);
    const sinTheta = Math.sin(thetaRef.current);
    const cosTheta = Math.cos(thetaRef.current);
    const cosYaw = Math.cos(yawRef.current);
    const sinYaw = Math.sin(yawRef.current);

    const northX = cosPhi * sinTheta, northY = -sinPhi, northZ = cosPhi * cosTheta;
    const eastX = cosTheta, eastZ = -sinTheta;

    const fwdX = -northX * cosYaw + eastX * sinYaw;
    const fwdY = -northY * cosYaw;
    const fwdZ = -northZ * cosYaw + eastZ * sinYaw;

    const nx = sinPhi * sinTheta;
    const ny = cosPhi;
    const nz = sinPhi * cosTheta;

    let newX = nx + fwdX * dTheta;
    let newY = ny + fwdY * dTheta;
    let newZ = nz + fwdZ * dTheta;
    const len = Math.sqrt(newX * newX + newY * newY + newZ * newZ);
    newX /= len; newY /= len; newZ /= len;

    phiRef.current = Math.acos(Math.max(-1, Math.min(1, newY)));
    thetaRef.current = Math.atan2(newX, newZ);
  }, []);

  const handleClick = useCallback(() => {
    if (!lockedRef.current) canvas.requestPointerLock();
  }, [canvas]);

  const handleLockChange = useCallback(() => {
    lockedRef.current = document.pointerLockElement === canvas;
  }, [canvas]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!lockedRef.current) return;
    yawRef.current -= e.movementX * MOUSE_SENSITIVITY;
    pitchRef.current += e.movementY * MOUSE_SENSITIVITY;
    pitchRef.current = Math.max(PITCH_MIN, Math.min(PITCH_MAX, pitchRef.current));
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const key = e.key.toLowerCase();
    if (key === 'w' || key === 'arrowup') keysRef.current.w = true;
    if (key === 's' || key === 'arrowdown') keysRef.current.s = true;
    if (key === 'a' || key === 'arrowleft') keysRef.current.a = true;
    if (key === 'd' || key === 'arrowright') keysRef.current.d = true;
    if (key === 'shift') keysRef.current.shift = true;
  }, []);

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    const key = e.key.toLowerCase();
    if (key === 'w' || key === 'arrowup') keysRef.current.w = false;
    if (key === 's' || key === 'arrowdown') keysRef.current.s = false;
    if (key === 'a' || key === 'arrowleft') keysRef.current.a = false;
    if (key === 'd' || key === 'arrowright') keysRef.current.d = false;
    if (key === 'shift') keysRef.current.shift = false;
    if (key === 'escape') {
      if (lockedRef.current) document.exitPointerLock();
    }
  }, []);

  useEffect(() => {
    camera.near = 0.1;
    camera.far = 30_000;
    (camera as THREE.PerspectiveCamera).updateProjectionMatrix();
  }, [camera]);

  useEffect(() => {
    canvas.addEventListener('click', handleClick);
    document.addEventListener('pointerlockchange', handleLockChange);
    document.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      canvas.removeEventListener('click', handleClick);
      document.removeEventListener('pointerlockchange', handleLockChange);
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (lockedRef.current) document.exitPointerLock();
    };
  }, [canvas, handleClick, handleLockChange, handleMouseMove, handleKeyDown, handleKeyUp]);

  useFrame((_, delta) => {
    const dt = Math.min(delta, 0.1);

    const sinPhi = Math.sin(phiRef.current);
    const cosPhi = Math.cos(phiRef.current);
    const sinTheta = Math.sin(thetaRef.current);
    const cosTheta = Math.cos(thetaRef.current);

    const nx = sinPhi * sinTheta;
    const ny = cosPhi;
    const nz = sinPhi * cosTheta;

    // Tangent basis (consistent reference frame on sphere)
    const eastX = cosTheta;
    const eastZ = -sinTheta;
    const northX = cosPhi * sinTheta;
    const northY = -sinPhi;
    const northZ = cosPhi * cosTheta;

    // Movement
    let moveForward = 0;
    let moveRight = 0;
    if (keysRef.current.w) moveForward += 1;
    if (keysRef.current.s) moveForward -= 1;
    if (keysRef.current.a) moveRight -= 1;
    if (keysRef.current.d) moveRight += 1;

    const isMoving = moveForward !== 0 || moveRight !== 0;
    const isSprinting = keysRef.current.shift && isMoving;
    const moveSpeed = isSprinting ? SPRINT_SPEED : WALK_SPEED;

    // Update walk animation
    isMovingRef.current = isMoving;
    if (isMoving) {
      walkPhaseRef.current += (isSprinting ? SPRINT_ANIM_SPEED : WALK_ANIM_SPEED) * dt;
    }

    if (isMoving) {
      const cosYaw = Math.cos(yawRef.current);
      const sinYaw = Math.sin(yawRef.current);

      const fwdX = -northX * cosYaw + eastX * sinYaw;
      const fwdY = -northY * cosYaw;
      const fwdZ = -northZ * cosYaw + eastZ * sinYaw;

      const rightX = eastX * cosYaw + northX * sinYaw;
      const rightY = northY * sinYaw;
      const rightZ = eastZ * cosYaw + northZ * sinYaw;

      let mx = fwdX * moveForward + rightX * moveRight;
      let my = fwdY * moveForward + rightY * moveRight;
      let mz = fwdZ * moveForward + rightZ * moveRight;
      const mLen = Math.sqrt(mx * mx + my * my + mz * mz);
      if (mLen > 0) { mx /= mLen; my /= mLen; mz /= mLen; }

      const oldFwdX = fwdX;
      const oldFwdY = fwdY;
      const oldFwdZ = fwdZ;

      const currentR = smoothGroundR.current > 0 ? smoothGroundR.current : sphereRadius;
      const angularStep = (moveSpeed * dt) / currentR;
      let newX = nx + mx * angularStep;
      let newY = ny + my * angularStep;
      let newZ = nz + mz * angularStep;
      const newLen = Math.sqrt(newX * newX + newY * newY + newZ * newZ);
      newX /= newLen; newY /= newLen; newZ /= newLen;

      phiRef.current = Math.acos(Math.max(-1, Math.min(1, newY)));
      thetaRef.current = Math.atan2(newX, newZ);

      // Parallel transport yaw
      const newSP = Math.sin(phiRef.current);
      const newCP = Math.cos(phiRef.current);
      const newST = Math.sin(thetaRef.current);
      const newCT = Math.cos(thetaRef.current);

      const newEastX = newCT;
      const newEastZ = -newST;
      const newNorthX = newCP * newST;
      const newNorthY = -newSP;
      const newNorthZ = newCP * newCT;

      const newSinYaw = oldFwdX * newEastX + oldFwdZ * newEastZ;
      const newCosYaw = -(oldFwdX * newNorthX + oldFwdY * newNorthY + oldFwdZ * newNorthZ);
      yawRef.current = Math.atan2(newSinYaw, newCosYaw);
    }

    // ── Position character ──
    const curST = Math.sin(thetaRef.current), curCT = Math.cos(thetaRef.current);
    const curSP = Math.sin(phiRef.current), curCP = Math.cos(phiRef.current);
    const cosYaw = Math.cos(yawRef.current), sinYaw = Math.sin(yawRef.current);

    const cnx = curSP * curST;
    const cny = curCP;
    const cnz = curSP * curCT;

    const terrainR = sampleTerrainRadius(cnx, cny, cnz, sphereRadius, seed);
    const targetGroundR = Math.max(terrainR, sphereRadius);

    if (smoothGroundR.current === 0 || isMoving) {
      smoothGroundR.current = targetGroundR;
    } else {
      smoothGroundR.current += (targetGroundR - smoothGroundR.current) * Math.min(1, dt * HEIGHT_LERP_RATE);
    }
    const groundR = smoothGroundR.current;

    _normal.set(cnx, cny, cnz);
    // Place feet at ground level (model origin is at feet, y=0)
    _charPos.copy(_normal).multiplyScalar(groundR + 0.05);

    // ── Compute forward direction from yaw (same basis as camera) ──
    const curEastX = curCT, curEastZ = -curST;
    const curNorthX = curCP * curST, curNorthY = -curSP, curNorthZ = curCP * curCT;

    _fwd.set(
      -curNorthX * cosYaw + curEastX * sinYaw,
      -curNorthY * cosYaw,
      -curNorthZ * cosYaw + curEastZ * sinYaw,
    );
    _right.crossVectors(_fwd, _normal).normalize();

    // ── Orient character using lookAt → faces _fwd direction, Y up = normal ──
    if (charRef.current) {
      charRef.current.position.copy(_charPos);

      _charLookTarget.copy(_charPos).add(_fwd);
      _lookAtMatrix.lookAt(_charPos, _charLookTarget, _normal);
      charRef.current.quaternion.setFromRotationMatrix(_lookAtMatrix);
    }

    // Expose to systems
    updatePlayerPosition(_charPos, _normal, thetaRef.current, phiRef.current, yawRef.current);
    combatNormalRef.current.copy(_normal);
    combatPosRef.current.copy(_charPos);
    combatFwdRef.current.copy(_fwd);
    combatRightRef.current.copy(_right);

    // ── Camera ──
    const cosPitch = Math.cos(pitchRef.current);
    const sinPitch = Math.sin(pitchRef.current);

    _camOffset.copy(_fwd).multiplyScalar(-CAM_DISTANCE * cosPitch)
      .addScaledVector(_normal, CAM_BASE_HEIGHT + CAM_DISTANCE * sinPitch);

    _targetCamPos.copy(_charPos).add(_camOffset);

    // Camera-terrain collision
    const camR = _targetCamPos.length();
    if (camR > 0) {
      const camNx = _targetCamPos.x / camR;
      const camNy = _targetCamPos.y / camR;
      const camNz = _targetCamPos.z / camR;
      const camTerrainR = sampleTerrainRadius(camNx, camNy, camNz, sphereRadius, seed);
      const camMinR = Math.max(camTerrainR, sphereRadius) + CAM_TERRAIN_CLEARANCE;
      if (camR < camMinR) {
        _targetCamPos.multiplyScalar(camMinR / camR);
      }
    }

    const posAlpha = 1 - Math.exp(-CAM_POS_LERP * dt);
    camera.position.lerp(_targetCamPos, posAlpha);

    camera.up.copy(_normal);
    _lookAtMatrix.lookAt(camera.position, _charPos, _normal);
    _targetCamQ.setFromRotationMatrix(_lookAtMatrix);
    camera.quaternion.slerp(_targetCamQ, 1 - Math.exp(-CAM_ROT_LERP * dt));
  }, -1);

  return (
    <>
      <group ref={charRef}>
        <CharacterModel walkPhase={walkPhaseRef} isMoving={isMovingRef} />
      </group>
      <CombatController
        charRef={charRef}
        theta={thetaRef.current}
        phi={phiRef.current}
        yaw={yawRef.current}
        normal={combatNormalRef.current}
        charPos={combatPosRef.current}
        charFwd={combatFwdRef.current}
        charRight={combatRightRef.current}
        sphereRadius={sphereRadius}
        onDodge={handleDodge}
      />
    </>
  );
}
